/**
 * The starting document a freshly created template is born with. `data` is required by
 * `POST /admin-api/assessment-templates` (§3), so the create modal mints one before the editor has
 * ever run. Deliberately empty, and not Zod-parsed (B9): a screenless assessment is legitimately
 * incomplete while it is being authored.
 */
import type { AssessmentTemplateDocument } from './types'
import type { DisciplineCode } from '@brighterly/lib-core-types/student-assessment/types'
import type { Grade } from './enums'

import { ID_PREFIX, mintId } from '@shared/lib/collections'

import { SCHEMA_VERSION } from '../../lib/assessments/schema'

/** The definition carries no `type` — that field lives on the row only (contract §2). */
interface EmptyAssessmentDocumentFields {
  title: string
  disciplineCode: DisciplineCode
  grade: Grade
}

/**
 * Builds `{ definition, rubrics }` for a brand-new template. The definition mirrors the four row
 * fields the admin entered, so document and row never disagree on day one. `rubrics` is an empty
 * catalog and comes back as `[]` on the next read (B7) — the API hooks normalise that on the way in.
 */
export const buildEmptyAssessmentDocument = (
  fields: EmptyAssessmentDocumentFields,
): AssessmentTemplateDocument => ({
  definition: {
    id: mintId(ID_PREFIX.assessment),
    schemaVersion: SCHEMA_VERSION,
    title: fields.title,
    disciplineCode: fields.disciplineCode,
    grade: fields.grade,
    screens: [],
  },
  rubrics: {},
})
